import React from 'react';
import {
    Button,
    Modal,
    ModalWidths,
    Text,
    TextAs,
    TextSizes,
    ButtonHierarchies,
} from '@clockout-dev/design-elements';
import SupportStatusPill from './SupportStatusPill';
import UserCardRow from '../../users/user/elements/UserCardRow';
import IssueStatusPill from '../../common/issueStatusPill/IssueStatusPill';
import IssueTypeTag from '../../common/issueTypeTag/IssueTypeTag';
import DateTimeCell from '../../reports/elements/DateTimeCell';
import { ISSUE_PANEL_COPY, ISSUE_TABLE_COPY, issueSummary } from '../issueTracker.constants';
import { DATE_TIME_SECONDS_FORMAT, TIMEZONE_NOTE } from '../../../utils/datetime';
import styles from '../styles/issue-detail-modal.module.scss';

/**
 * Read-only detail for one mirrored issue, opened by clicking a row or a card.
 *
 * A modal rather than a side panel: the table is eight columns wide and the
 * board scrolls horizontally, so a permanent side column would squeeze the thing
 * the FI came to read. It also means both views open the same detail the same
 * way, instead of the board having to grow a second layout.
 *
 * Deliberately carries NO link out to Jira: the FI has no Jira access, so a
 * "View in Jira" affordance would only ever produce a permission error — and the
 * story is explicit that no Jira URL reaches the browser.
 *
 * This is where the SECOND level of the vocabulary surfaces. The table column
 * and the board column show the coarse bucket (`status`) because five values is
 * what a queue can be scanned by; the fine-grained `stage` (In review, Testing,
 * Awaiting deployment, …) answers "how far along is it really" and belongs here.
 * Both come from the served map, so neither is spelled in this file.
 *
 * Footer is `null`: there is nothing to confirm or cancel, only to close.
 *
 * Opened from two places — a row or card on the Jira tab, and the linked issue
 * on a support ticket — so it is driven by `issueKey` (what is open) rather than
 * by the record itself. The record arrives from the detail endpoint a moment
 * later, which is why `loading` exists: a click opens the modal immediately
 * instead of hanging until the response lands.
 *
 * @param {Object} props
 * @param {string|null} props.issueKey the open issue's key; `null` keeps it closed
 * @param {object|null} props.issue the fetched detail, once it arrives
 * @param {boolean} [props.loading]
 * @param {boolean} [props.error]
 * @param {() => void} props.onClose
 * @param {(ticketId: string) => void} [props.onOpenTicket] navigate to a ticket
 * @param {(statusKey: string) => object|undefined} [props.resolveStatusEntry]
 * @param {(stageKey: string) => string|null} [props.resolveStageLabel]
 */
function IssueDetailModal({
    issueKey,
    issue,
    loading = false,
    error = false,
    onClose,
    onOpenTicket,
    resolveStatusEntry,
    resolveStageLabel,
}) {
    const tickets = issue?.supportTickets || [];
    const dateRow = (label, value, neverLabel) => (
        <UserCardRow
            label={label}
            value={
                <DateTimeCell value={value} format={DATE_TIME_SECONDS_FORMAT} {...(neverLabel ? { neverLabel } : {})} />
            }
        />
    );

    return (
        <Modal
            open={Boolean(issueKey)}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            modalWidth={ModalWidths.LARGE}
            title={
                <div className={styles.header}>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.headerKey}>
                        {issue?.key || issueKey}
                    </Text>
                    <IssueTypeTag type={issue?.issueType} />
                    <IssueStatusPill status={issue?.status} servedEntry={resolveStatusEntry?.(issue?.status)} />
                </div>
            }
        >
            {/* The key is already in the header, so a pending or failed fetch
                still tells the reader which issue they opened. */}
            {loading ? (
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.pending}>
                    {ISSUE_PANEL_COPY.LOADING}
                </Text>
            ) : null}

            {error ? (
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.failed} role="alert">
                    {ISSUE_PANEL_COPY.LOAD_FAILED}
                </Text>
            ) : null}

            {!loading && !error && issue ? (
                <div className={styles.body}>
                    <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.summary}>
                        {issueSummary(issue.summary)}
                    </Text>

                    <div className={styles.rows}>
                        <UserCardRow
                            label={ISSUE_PANEL_COPY.FIELD_STAGE}
                            value={resolveStageLabel?.(issue.stage) || issue.stage}
                        />
                        <UserCardRow
                            label={ISSUE_PANEL_COPY.FIELD_ASSIGNEE}
                            value={issue.assignee || ISSUE_TABLE_COPY.UNASSIGNED}
                        />
                        <UserCardRow label={ISSUE_PANEL_COPY.FIELD_PRIORITY} value={issue.priority} />
                        {dateRow(ISSUE_PANEL_COPY.FIELD_CREATED, issue.createdAt)}
                        {dateRow(ISSUE_PANEL_COPY.FIELD_STARTED, issue.startedAt, ISSUE_TABLE_COPY.NOT_STARTED)}
                        {dateRow(ISSUE_PANEL_COPY.FIELD_UPDATED, issue.lastUpdatedAt)}
                        {dateRow(ISSUE_PANEL_COPY.FIELD_RESOLVED, issue.resolvedAt, ISSUE_TABLE_COPY.NOT_RESOLVED)}
                    </div>

                    {/* Which of MY requests is this work for. The server scopes
                        this to the viewer's institution: an issue is routinely
                        linked to tickets from several FIs, and none of them may
                        see the others'.

                        Each ticket also carries `linkedAt`. It is deliberately
                        not drawn: it stamps when STAFF attached the issue, not
                        anything the FI did, and a date beside a subject would be
                        read as the ticket's own — which it is not. */}
                    <section className={styles.tickets}>
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.ticketsTitle}>
                            {ISSUE_PANEL_COPY.TICKETS_TITLE}
                        </Text>

                        {tickets.length === 0 ? (
                            <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.ticketsEmpty}>
                                {ISSUE_PANEL_COPY.TICKETS_NONE}
                            </Text>
                        ) : (
                            <ul className={styles.ticketList}>
                                {tickets.map((ticket) => (
                                    <li key={ticket.id} className={styles.ticketItem}>
                                        <Button
                                            hierarchy={ButtonHierarchies.LINK_COLOR}
                                            className={styles.ticketLink}
                                            onClick={() => onOpenTicket?.(ticket.id)}
                                        >
                                            {ticket.subject}
                                        </Button>
                                        <SupportStatusPill status={ticket.status} />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.timezoneNote}>
                        {TIMEZONE_NOTE}
                    </Text>
                </div>
            ) : null}
        </Modal>
    );
}

export default IssueDetailModal;
